import { TextField } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react'

import { numberWithCommas } from '../../utils'
import { FormError, Payment } from '../../types'
import DangerIcon from '../../assets/icons/danger-icon.svg'
import { textFieldWhiteBg } from '../../theme/custom-styles'
import WarningIcon from '../../assets/icons/warning-icon.svg'

export const PaymentAmountNoveltyForm = forwardRef(({ errors, paymentData }: { errors: FormError; paymentData: Payment | Partial<Payment> | null }, ref) => {
  const { t } = useTranslation(['payments-to-review-management', 'common'])

  const [newPaymentAmount, setNewPaymentAmount] = useState<string>('')
  const [paymentAmountNoveltiesData, setPaymentAmountNoveltiesData] = useState<
    Array<{
      message: string
      severity: string
    }>
  >([])

  useImperativeHandle(
    ref,
    () => {
      return {
        newPaymentAmount
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [newPaymentAmount]
  )

  useEffect(() => {
    if (paymentData && Array.isArray(paymentData.novelties)) {
      const paymentAmountNoveltiesDataToDisplay = []

      for (let i = 0; i < paymentData.novelties.length; i++) {
        const paymentNovelty = paymentData.novelties[i]
        const paymentReferenceNoveltyToDisplayData = {
          message: '',
          severity: ''
        }

        if (paymentNovelty.type === 'empty-payment-amount') {
          paymentReferenceNoveltyToDisplayData.severity = 'danger'
          paymentReferenceNoveltyToDisplayData.message = t('noveltiesDescription.emptyPaymentAmount')

          paymentAmountNoveltiesDataToDisplay.push(paymentReferenceNoveltyToDisplayData)
        }

        if (paymentNovelty.type === 'wrong-payment-amount') {
          paymentReferenceNoveltyToDisplayData.severity = 'danger'
          paymentReferenceNoveltyToDisplayData.message = t('noveltiesDescription.wrongPaymentAmount')

          paymentAmountNoveltiesDataToDisplay.push(paymentReferenceNoveltyToDisplayData)
        }
      }

      setPaymentAmountNoveltiesData(paymentAmountNoveltiesDataToDisplay)

      if (paymentData.amount) {
        setNewPaymentAmount(String(paymentData.amount))
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentData])

  return (
    <div
      style={{ borderBottom: 'none' }}
      className="document-findings-edit-bank-account-row-container"
    >
      {Array.isArray(paymentAmountNoveltiesData) && paymentAmountNoveltiesData.length > 0 && (
        <div className="novelty-modal-content-container">
          {paymentAmountNoveltiesData.map(
            (
              paymentReferenceNovelty: {
                message: string
                severity: string
              },
              index: number
            ) => (
              <div
                key={index}
                className="info-data-container"
                style={{ alignItems: 'center', justifyContent: 'center' }}
              >
                <div className="icon-container">
                  <img
                    src={paymentReferenceNovelty.severity === 'danger' ? DangerIcon : WarningIcon}
                    alt="novelty-icon"
                  />
                </div>
                <div
                  className="info-data-text"
                  style={paymentReferenceNovelty.severity === 'danger' ? { color: '#EF4444' } : { color: '#F59E0B' }}
                >
                  {paymentReferenceNovelty.message}
                </div>
              </div>
            )
          )}
        </div>
      )}
      <TextField
        fullWidth
        variant="standard"
        sx={textFieldWhiteBg}
        value={newPaymentAmount}
        error={!!errors.errorPaymentAmount}
        helperText={errors.errorPaymentAmount}
        onBlur={(event) => {
          if (event.target.value.trim() === '$') {
            setNewPaymentAmount('')
            return
          }

          const extractedNumber = Number(event.target.value.slice(1).trim().replaceAll(',', ''))
          setNewPaymentAmount(`$ ${numberWithCommas(extractedNumber)}`)
        }}
        onChange={(event) => {
          const target = event.target

          if (event.target.value.length === 0 && isNaN(Number(event.target.value))) {
            target.value = ''
            return target.value
          }

          const input = event.target.value.replace(/\D/g, '')

          if (input.length > 0) {
            target.value = `$ ${numberWithCommas(Number(input))}`
          }

          setNewPaymentAmount(target.value)
        }}
        label={`${t('resolveNoveltyModal.paymentAmount.newPaymentAmount')}`}
      />
    </div>
  )
})
